/**
 * The instrument frame every Legends chart is drawn on: the grid, the two axes,
 * the tick numbers and the two captions that name them.
 *
 * ONE definition, read by two renderers that share no other line of code. The
 * car's page draws its bands as JSX into a viewBox and lets CSS colour them; the
 * share card draws its chart as SVG strings straight into card pixels with a
 * palette passed in. Those are genuinely different jobs and neither can be
 * written in terms of the other — but where the gridlines fall, where a tick
 * number sits against its axis and where a caption goes are the same question in
 * both, and the whole proposition of a share card is that it is the same product
 * seen from outside.
 *
 * They had drifted, which is why this exists. The card turned its speed caption
 * on its side in the gutter and set its time caption on the bottom-left baseline
 * beside the tick numbers; the band set both as HTML paragraphs above and below
 * the plot, in a different case, at a different size. Same chart, same car, two
 * pictures — and nothing failed, because nothing was comparing them.
 *
 * THE CARD NO LONGER DRAWS AN ACCELERATION TRACE — that panel came off, and the
 * card carries the value history alone. This module outlived its first reason
 * and is not weaker for it: what it now holds together is every chart in the
 * product, on a page and on a card, drawn on one instrument. The two consumers
 * plot different data, which is exactly the case a shared FRAME is for.
 *
 * ## What this module is and is not
 *
 * It answers WHERE, in whatever coordinate space the caller is working in. It
 * has no opinion about colour, stroke, font or markup, and it never emits any:
 * a `<line>` with a CSS class and a `<line stroke="#333">` are the same geometry
 * and the difference between them belongs to the renderer.
 *
 * Everything takes a `plot` — the drawing box, `{ x, y, width, height }` — and a
 * `project` — `{ x(value), y(value) }` from data space into that box. Both
 * callers already had those; nothing here needs a viewBox, a card, or a DOM.
 *
 * `domain/` because it is pure arithmetic that both `ui/` and `scripts/` have to
 * agree on, which is the same reason `axisTicks` lives in
 * `domain/cars/accelerationTrace.js` — a tick ladder is drawing arithmetic too,
 * and it has been in the domain since the band was written. The card reaches it
 * through `scripts/lib/domainModule.js`, exactly as it reaches the rest.
 */

/**
 * How far a tick number sits off the axis it labels.
 *
 * The one number in this file that is a taste rather than a consequence, and it
 * is shared for that reason: two charts a reader sees side by side with
 * different gaps look like two charts drawn by different people.
 */
export const TICK_GAP = 6;

/**
 * How far the vertical caption sits from the left edge of the gutter, as a
 * fraction of the tick type size.
 *
 * A rotated line of text is as wide as its font is tall, so a shade under one
 * character height puts its spine in the middle of the column with room on both
 * sides — enough that it never touches the panel edge, little enough that the
 * tick numbers still clear it.
 */
const CAPTION_SPINE = 0.9;

/**
 * The optical centre of a line of tabular figures, as a fraction of its size.
 *
 * A `<text>` baseline sits under the digits, so a tick number anchored at its
 * gridline's own y reads as though it has slipped upward. Roughly a third of the
 * cap height puts the digits' middle on the line, which is what a reader is
 * actually comparing against.
 */
const TICK_CENTRING = 0.35;

/**
 * The two axes a reader measures against, as one path.
 *
 * Drawn as a single open path from the top of the y axis, down to the origin and
 * out along the x axis — so the corner is a JOIN rather than two strokes that
 * happen to meet. At the hairline weights both charts use, two abutting lines
 * leave a visible notch exactly where the origin is, which is the one point on
 * the frame that has to look deliberate.
 *
 * A string rather than points, because both renderers want a `d` and composing
 * it twice is how a corner ends up drawn two ways.
 */
export function axisPath(plot) {
    return `M${plot.x} ${plot.y} L${plot.x} ${plot.y + plot.height} L${plot.x + plot.width} ${plot.y + plot.height}`;
}

/**
 * Where the gridlines fall.
 *
 * Every line spans the whole plot, so the grid reads as a ruled field rather
 * than as ticks growing out of the axes — it is the 8-bit car screen this whole
 * band is a reference to, and that screen ruled the box.
 *
 * The `value` rides along on each line because a renderer needs a stable key for
 * it, and the tick's own value is the only thing about a gridline that is
 * unique.
 */
export function gridLines({ plot, project, xTicks = [], yTicks = [] }) {
    return {
        horizontal: yTicks.map((value) => ({
            value,
            x1: plot.x,
            x2: plot.x + plot.width,
            y: project.y(value),
        })),
        vertical: xTicks.map((value) => ({
            value,
            x: project.x(value),
            y1: plot.y,
            y2: plot.y + plot.height,
        })),
    };
}

/**
 * Where each tick number goes.
 *
 * The y numbers sit OUTSIDE the plot in the left gutter, right-aligned against
 * the axis; the x numbers sit below it, centred on their gridline. That is the
 * margin arrangement the reference screens used, and it is the reason both
 * charts carry a left inset wide enough for three digits.
 *
 * `tickSize` is the type size the renderer will actually set these at, and it is
 * a parameter rather than a constant because the card draws its chart at
 * three sizes. It is used for the optical centring only — nothing here measures
 * text, because a tick number is at most four tabular characters and the gutter
 * is sized for the widest of them by construction.
 */
export function tickAnchors({
    plot, project, xTicks = [], yTicks = [], tickSize,
}) {
    return {
        y: yTicks.map((value) => ({
            value,
            x: plot.x - TICK_GAP,
            y: project.y(value) + tickSize * TICK_CENTRING,
            anchor: 'end',
        })),
        x: xTicks.map((value) => ({
            value,
            x: project.x(value),
            y: plot.y + plot.height + tickSize + TICK_GAP,
            anchor: 'middle',
        })),
    };
}

/**
 * Where the two captions go — the names of the axes, not their numbers.
 *
 * THE VERTICAL ONE IS TURNED ON ITS SIDE IN THE GUTTER, which is the one region
 * of a panel this small that is reliably empty. Laid flat it has to go either in
 * the header — where it crowds the heading and the verdict badge, and did:
 * "Value (USD)" ran into "AS AN INVESTMENT" on the Countach — or inside the
 * plot, where which corner is empty depends on the data, and a car whose launch
 * price was its high point has ink in every corner a caption could use.
 *
 * THE HORIZONTAL ONE GOES IN THE BOTTOM-LEFT CORNER, below the column of y
 * numbers and left of the first x number, on the same baseline as the x numbers.
 * The first gridline never reaches that corner, so it is empty for every dataset
 * rather than for most of them.
 *
 * `gutterLeft` is where the caption column starts in the caller's own
 * coordinates — the panel's inner edge on a card, the viewBox origin in a
 * component. Everything else is derived from it and from the plot, so a caller
 * that has already decided its insets does not get to decide these twice.
 */
export function captionAnchors({ plot, gutterLeft, tickSize }) {
    const spine = gutterLeft + Math.round(tickSize * CAPTION_SPINE);
    const middle = plot.y + plot.height / 2;

    return {
        // Rotated about its own anchor, so the caller writes one transform and
        // never has to work out where the text ended up.
        y: {
            x: spine,
            y: middle,
            rotate: -90,
            anchor: 'middle',
        },
        x: {
            x: gutterLeft,
            y: plot.y + plot.height + tickSize + TICK_GAP,
            anchor: 'start',
        },
    };
}
